import { useEffect, useState } from 'react'
import useMediaQuery from 'hooks/useMediaQuery'
import { MOBILE_WIDTH } from 'utils/constants'
import Skeleton from 'react-loading-skeleton'
import { ProductTiers } from 'components/ProgressiveDiscount/ProductTiers'
import * as Style from './styles'
import 'react-loading-skeleton/dist/skeleton.css'
import { useOrderB2c } from '../../../hooks/useOrderB2c'

const quantityOptions = Array.from({ length: 70 }, (_, i) => i + 1)

interface Item {
  id: number
  name: string
  real_price: number
  quantity: number
  image: string
  amount?: number
  promotional?: boolean
  discount?: number
  promotion?: {
    discount_value?: string | number
    discount_type?: string
  }
  productId?: number
  onQuantityChange?: (id: number, quantity: number) => Promise<void>
}

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-br', {
    style: 'currency',
    currency: 'BRL',
    currencyDisplay: 'symbol',
  }).format(value)

export const Product = ({
  id,
  image,
  name,
  quantity,
  real_price,
  amount,
  promotional,
  discount,
  promotion,
  productId,
  onQuantityChange,
}: Item) => {
  const { removeCart, setQuantity, couponIsValid, couponResponse, getDiscount, itens } = useOrderB2c()
  const isMobile = useMediaQuery(MOBILE_WIDTH)
  const [updating, setUpdating] = useState(false)

  async function handleSelect(value: string) {
    const newQuantity = parseInt(value, 10)
    if (newQuantity === quantity) return
    if (newQuantity === 0) {
      removeCart(id)
      return
    }
    setQuantity(id, newQuantity)
    if (onQuantityChange) {
      setUpdating(true)
      try {
        await onQuantityChange(id, newQuantity)
      } finally {
        setUpdating(false)
      }
    }
  }

  useEffect(() => {
    if (couponIsValid) {
      getDiscount(itens, couponResponse.coupon.name)
    }
  }, [quantity])

  const hasPromotion = !!promotional && Number(discount) > 0 && quantity > 0
  const promotionalUnitPrice = hasPromotion && amount !== undefined ? amount / quantity : real_price
  const total = hasPromotion && amount !== undefined ? amount : real_price * quantity

  const buildDiscountLabel = () => {
    if (promotion?.discount_value === undefined || !promotion?.discount_type) return null
    if (promotion.discount_type === 'percent') return `-${Number(promotion.discount_value)}%`
    return `-${formatBRL(Number(promotion.discount_value))}`
  }
  const discountLabel = buildDiscountLabel()

  return (
    <Style.Container key={id} isMobile={isMobile}>
      <Style.ProductInfos isMobile={isMobile}>
        {!image ? <Skeleton style={{ width: '60px', height: '60px' }} /> : <img src={image} alt="foto produto" />}
        {!name ? (
          <Skeleton style={{ width: '250px', marginLeft: '20px' }} />
        ) : (
          <div>
            <Style.ProductName isMobile={isMobile}>{name}</Style.ProductName>
            <ProductTiers productId={productId} />
          </div>
        )}
      </Style.ProductInfos>

      <Style.ProductProperties isMobile={isMobile}>
        {!quantity ? (
          <Skeleton style={{ width: '50px', height: '50px' }} />
        ) : (
          <Style.Quantity>
            <select value={quantity} disabled={updating} onChange={e => handleSelect(e.target.value)}>
              {quantityOptions.map(opt => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </Style.Quantity>
        )}
        {!real_price && real_price !== 0 ? (
          <Skeleton style={{ width: '50px' }} />
        ) : (
          <Style.ProductPrice isMobile={isMobile}>
            {isMobile && <span>VALOR UNITÁRIO</span>}
            {hasPromotion ? (
              <>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center' }}>
                  {discountLabel && (
                    <span
                      style={{
                        background: '#115850',
                        color: '#fff',
                        padding: '1px 6px',
                        borderRadius: '4px',
                        fontSize: '11px',
                      }}
                    >
                      {discountLabel}
                    </span>
                  )}
                  <span style={{ textDecoration: 'line-through', color: '#115850', fontSize: '12px' }}>
                    {formatBRL(real_price)}
                  </span>
                </div>
                <div>{formatBRL(promotionalUnitPrice)}</div>
              </>
            ) : (
              formatBRL(real_price)
            )}
          </Style.ProductPrice>
        )}
        {!total && real_price !== 0 ? (
          <Skeleton style={{ width: '50px' }} />
        ) : (
          <Style.ProductTotal isMobile={isMobile}>
            {isMobile && <span>VALOR TOTAL</span>}
            {formatBRL(total)}
          </Style.ProductTotal>
        )}
      </Style.ProductProperties>
    </Style.Container>
  )
}
